import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UnderlinedSectionHeader from "../UnderlinedSectionHeader";
import clsx from "clsx";

function AccordionItem({ item, isOpen, onClick, index }) {
    return (
        <div className={styles.itemContainer}>
            <motion.div
                onClick={() => onClick(index)}
                className={styles.itemHeader}
            >
                <div className="w-[90%] font-bold text-base md:text-2xl">
                    {item.title}
                </div>
                <div className="h-[15px] w-[10%] flex justify-end items-center">
                    <motion.div
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="text-2xl font-bold leading-none"
                    >
                        {isOpen ? "−" : "+"}
                    </motion.div>
                </div>
            </motion.div>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                            type: "spring",
                            duration: 0.3,
                            bounce: 0,
                        }}
                    >
                        <div className="pb-6 pt-4 font-body text-base leading-7 text-darkGray md:text-lg md:leading-8">
                            {item.body}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function LifeCycleAccordion({
    title,
    subheader,
    items,
    defaultOpenIndex = 0,
}) {
    // Always keep one item open - ensure defaultOpenIndex is valid
    const safeDefaultIndex = Math.min(defaultOpenIndex, items.length - 1);
    const [openIndex, setOpenIndex] = useState(Math.max(0, safeDefaultIndex));
    
    // Always ensure we have a valid currentItem (one is always open)
    const currentItem = items[openIndex] || items[0];

    const handleItemClick = (index) => {
        // Only allow one item to be open at a time
        // Always keep one item open - clicking an item opens it
        setOpenIndex(index);
    };

    return (
        <section className={styles.section}>
            <div className={styles.headerContainer}>
                <UnderlinedSectionHeader className="mx-auto text-base md:text-[42px]">
                    {title}
                </UnderlinedSectionHeader>
                {subheader && <p className={styles.subheader}>{subheader}</p>}
            </div>

            <div className={styles.contentContainer}>
                {/* DESKTOP */}
                <div className="hidden md:flex w-full gap-8 items-stretch relative">
                    {/* Image - Left Side */}
                    <div className={styles.imageContainer}>
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={currentItem?.id || openIndex}
                                src={currentItem?.image}
                                alt={currentItem?.imageAlt || currentItem?.title}
                                className={styles.image}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2, ease: "easeInOut" }}
                                loading="lazy"
                            />
                        </AnimatePresence>
                    </div>

                    {/* Accordion - Right Side */}
                    <div className={styles.accordionContainer}>
                        {items.map((item, index) => (
                            <AccordionItem
                                key={item.id || index}
                                item={item}
                                isOpen={openIndex === index}
                                onClick={handleItemClick}
                                index={index}
                            />
                        ))}
                    </div>
                </div>

                {/* MOBILE */}
                <div className="flex md:hidden w-full flex-col gap-8">
                    {/* Image - Top on Mobile */}
                    {currentItem && (
                        <div className={styles.imageContainer}>
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={currentItem?.id || openIndex}
                                    src={currentItem?.image}
                                    alt={
                                        currentItem?.imageAlt ||
                                        currentItem?.title
                                    }
                                    className={styles.image}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                    loading="lazy"
                                />
                            </AnimatePresence>
                        </div>
                    )}

                    {/* Accordion - Bottom on Mobile */}
                    <div className={styles.accordionContainer}>
                        {items.map((item, index) => (
                            <AccordionItem
                                key={item.id || index}
                                item={item}
                                isOpen={openIndex === index}
                                onClick={handleItemClick}
                                index={index}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

const styles = {
    section: clsx(
        "mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 md:px-10 lg:px-0",
    ),
    headerContainer: clsx("flex flex-col items-center gap-6 text-center"),
    subheader: clsx(
        "mx-auto max-w-3xl font-body text-base leading-7 text-darkGray md:text-xl md:leading-9",
    ),
    contentContainer: clsx("w-full mt-6"),
    imageContainer: clsx(
        "w-full md:w-1/2 aspect-square flex items-center justify-center relative",
    ),
    image: clsx(
        "w-full h-full object-cover rounded-lg border border-borderColor shadow-md",
    ),
    accordionContainer: clsx(
        "flex flex-col w-full md:w-1/2 rounded-lg bg-gray p-6 md:p-8 shadow-sm",
    ),
    itemContainer: clsx(
        "w-full border-b border-black/40 py-4 last:border-b-0",
    ),
    itemHeader: clsx(
        "flex cursor-pointer items-center justify-between w-full hover:text-black transition-colors",
    ),
};

